import { useState } from "react";

import type { UploadFileExtended } from "@av/apps/public/jobs/services/apply/types";
import {
  type DocumentUploadValidationError,
  getDocumentUploadValidationError,
} from "@av/apps/recruitment/ats-common/services/document-upload-validation";

import type { RcFile, UploadFile } from "antd/es/upload/interface";

export type SupportingDocumentDraft = UploadFileExtended;

export type SupportingDocumentValidationIssue = {
  validationError: DocumentUploadValidationError;
  fileNames: string[];
};

type InitialReplacement = {
  fileUid: string;
  replacementFile: UploadFile;
};

function toSupportingDocumentDraft(file: UploadFile): SupportingDocumentDraft {
  return {
    ...file,
    uid: file.uid,
    name: file.name,
    size: file.size,
    type: file.type,
    originFileObj: file.originFileObj ?? (file instanceof File ? (file as RcFile) : undefined),
    documentType: "SUPPORTING_DOCUMENT",
  };
}

function getInitialDraftFiles(
  fileList: UploadFileExtended[],
  initialReplacement?: InitialReplacement
) {
  const replacementDraft = initialReplacement
    ? toSupportingDocumentDraft(initialReplacement.replacementFile)
    : undefined;
  const validationIssues = new Map<DocumentUploadValidationError, string[]>();
  const draftFiles = fileList.flatMap((file) => {
    const nextFile =
      replacementDraft && file.uid === initialReplacement?.fileUid
        ? replacementDraft
        : toSupportingDocumentDraft(file);
    const validationError = getDocumentUploadValidationError(nextFile);

    if (validationError) {
      const nextFileNames = validationIssues.get(validationError) ?? [];

      nextFileNames.push(nextFile.name);
      validationIssues.set(validationError, nextFileNames);

      return [];
    }

    return [nextFile];
  });

  return {
    draftFiles,
    validationIssues: Array.from(validationIssues.entries()).map(
      ([validationError, fileNames]) => ({
        validationError,
        fileNames,
      })
    ),
  };
}

export function useSupportingDocumentDrafts(
  fileList: UploadFileExtended[],
  initialReplacement?: InitialReplacement
) {
  const initialState = getInitialDraftFiles(fileList, initialReplacement);
  const [draftFiles, setDraftFiles] = useState(initialState.draftFiles);

  return {
    committedFiles: draftFiles,
    draftFiles,
    validationIssues: initialState.validationIssues,
    replaceSelectedFiles: (files: UploadFile[]) => {
      setDraftFiles(files.map(toSupportingDocumentDraft));
    },
  };
}
